import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const LegalLayout = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen flex-col overflow-x-clip bg-white text-brand-black">
    <Header />
    <main className="flex-1">
      <section className="border-b border-gray-100 bg-gradient-to-b from-blue-50/40 to-white">
        <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand-grayMid hover:text-brand-blue"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-emerald-800">
            <ShieldCheck className="h-3.5 w-3.5" /> Legal
          </p>
          <h1 className="mt-3 text-4xl font-black uppercase leading-[1.05] tracking-tight text-brand-black sm:text-5xl">
            {title}
          </h1>
          {subtitle && <p className="mt-3 text-sm text-brand-grayMid">{subtitle}</p>}
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
        <article className="prose-7fc">{children}</article>
      </section>
    </main>
    <Footer />
    <style>{`
      .prose-7fc h2 {
        font-size: 1.25rem;
        font-weight: 900;
        text-transform: uppercase;
        letter-spacing: -0.01em;
        color: #0B0B0F;
        margin-top: 2.25rem;
        margin-bottom: 0.75rem;
      }
      .prose-7fc h3 {
        font-size: 0.95rem;
        font-weight: 800;
        color: #0B0B0F;
        margin-top: 1.25rem;
        margin-bottom: 0.4rem;
      }
      .prose-7fc p, .prose-7fc li {
        font-size: 0.95rem;
        line-height: 1.65;
        color: #374151;
      }
      .prose-7fc p { margin: 0.6rem 0; }
      .prose-7fc ul { margin: 0.6rem 0 0.6rem 1.25rem; list-style: disc; }
      .prose-7fc strong { color: #0B0B0F; font-weight: 700; }
      .prose-7fc a { color: #1A56FF; font-weight: 600; }
      .prose-7fc a:hover { text-decoration: underline; }
    `}</style>
  </div>
);

export const PrivacyPage = () => {
  useSEO({
    title: "Privacy Policy — The 7 Figure Cleaner",
    description:
      "How The 7 Figure Cleaner collects, uses and protects your information across our website and services.",
    path: "/privacy",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const updated = "27 June 2026";
  return (
    <LegalLayout title="Privacy Policy" subtitle={`Last updated: ${updated}`}>
      <p>
        This Privacy Policy describes how <strong>Chris Andrews trading as The 7 Figure Cleaner</strong>{" "}
        (<strong>ABN 17 639 251 027</strong>) (&quot;<strong>we</strong>,&quot; &quot;<strong>us</strong>,&quot; &quot;<strong>our</strong>&quot;) collects, uses, stores and discloses your personal information when you interact with us through{" "}
        <strong>7figurecleaner.com.au</strong> (the &quot;Site&quot;), our services, our ads, or any other channel.
      </p>
      <p>
        We are committed to handling your personal information in line with the Australian Privacy Principles (APPs) contained in the <em>Privacy Act 1988</em> (Cth).
      </p>

      <h2>1. What information we collect</h2>
      <p>We may collect the following personal information from you:</p>
      <ul>
        <li>Your name, business name, email address and phone number;</li>
        <li>The service area, suburbs or geographic territory in which you operate;</li>
        <li>Information about your business (website, services offered, monthly revenue, business goals);</li>
        <li>Any messages, enquiries or qualification answers you submit through our forms;</li>
        <li>Technical data such as IP address, device type, browser, referring URL, pages viewed and other analytics data collected via cookies and tracking pixels (see Section 5).</li>
      </ul>

      <h2>2. How we collect it</h2>
      <p>We collect personal information directly from you when you:</p>
      <ul>
        <li>Submit our territory check / qualification form on the Site;</li>
        <li>Send us a message through our contact form, email or phone;</li>
        <li>Book a strategy call through our scheduling system;</li>
        <li>Subscribe to or receive marketing communications from us.</li>
      </ul>
      <p>
        We also collect information automatically through cookies, analytics tools and advertising pixels operated by us or our advertising partners.
      </p>

      <h2>3. Why we use your information</h2>
      <p>We use your personal information to:</p>
      <ul>
        <li>Assess whether your business is a fit for our service and whether your territory is available;</li>
        <li>Respond to enquiries and provide quotes;</li>
        <li>Deliver and manage our lead-generation service (including running Meta Ads on your behalf);</li>
        <li>Communicate with you about your account, including updates, performance and billing;</li>
        <li>Send marketing communications about our services, offers and content (you can opt out at any time);</li>
        <li>Improve the Site, our ads, our offers and the overall user experience;</li>
        <li>Comply with our legal obligations.</li>
      </ul>

      <h2>4. Who we share your information with</h2>
      <p>We do not sell your personal information. We may share it with:</p>
      <ul>
        <li><strong>Service providers</strong> who help us operate our business — including email/SMS platforms, CRM, scheduling, hosting and analytics providers — solely to the extent needed to deliver our services to you;</li>
        <li><strong>Advertising platforms</strong> such as Meta (Facebook/Instagram) and Google, to deliver, measure and optimise advertising;</li>
        <li><strong>Government bodies or regulators</strong> where required by law;</li>
        <li><strong>Professional advisors</strong> such as accountants and lawyers, where reasonably necessary.</li>
      </ul>

      <h2>5. Cookies and tracking technologies</h2>
      <p>
        Our Site uses cookies and similar technologies (including the Meta Pixel and Google Analytics / Google Tag Manager) to understand site usage, measure ad performance and improve our marketing. You can disable cookies in your browser settings, but some parts of the Site may not function as intended.
      </p>

      <h2>6. SMS &amp; email communications</h2>
      <p>
        If you opt in to SMS or email communications, you agree to receive transactional and promotional messages from us. Message frequency varies. Message and data rates may apply. You can reply <strong>STOP</strong> to unsubscribe from SMS, or use the unsubscribe link in any email.
      </p>

      <h2>7. Storage and security</h2>
      <p>
        We take reasonable steps to protect your personal information from misuse, loss, unauthorised access, modification or disclosure. Information is stored on secure servers operated by trusted service providers, some of which may be located outside Australia. By submitting personal information you consent to that transfer.
      </p>

      <h2>8. Accessing or correcting your information</h2>
      <p>
        You can request access to, correction of, or deletion of the personal information we hold about you by emailing us at{" "}
        <a href="mailto:hello@7figurecleaner.com.au">hello@7figurecleaner.com.au</a>. We will respond within a reasonable period.
      </p>

      <h2>9. Complaints</h2>
      <p>
        If you believe we have breached your privacy, please contact us first so we can try to resolve it. If you remain unsatisfied, you may contact the Office of the Australian Information Commissioner (OAIC) at{" "}
        <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">oaic.gov.au</a>.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. The latest version will always be available on this page with the &quot;Last updated&quot; date.
      </p>

      <h2>11. Contact us</h2>
      <p>
        <strong>The 7 Figure Cleaner</strong> &middot; Chris Andrews<br />
        ABN 17 639 251 027<br />
        Carnegie VIC 3163, Melbourne, Australia<br />
        Email: <a href="mailto:hello@7figurecleaner.com.au">hello@7figurecleaner.com.au</a>
      </p>
    </LegalLayout>
  );
};

export const TermsPage = () => {
  useSEO({
    title: "Terms of Service — The 7 Figure Cleaner",
    description:
      "Terms and conditions for The 7 Figure Cleaner's done-for-you exterior-cleaning service, including our 30 exclusive local enquiries in 30 days guarantee.",
    path: "/terms",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const updated = "27 June 2026";
  return (
    <LegalLayout title="Terms of Service" subtitle={`Last updated: ${updated}`}>
      <p>
        These Terms of Service (the &quot;<strong>Terms</strong>&quot;) govern your use of the website <strong>7figurecleaner.com.au</strong>{" "}
        and any services provided by <strong>Chris Andrews trading as The 7 Figure Cleaner</strong> (<strong>ABN 17 639 251 027</strong>) (&quot;<strong>we</strong>,&quot; &quot;<strong>us</strong>,&quot; &quot;<strong>our</strong>&quot;). By accessing the Site or engaging our services, you agree to these Terms.
      </p>

      <h2>1. The service</h2>
      <p>
        We provide a complete customer-acquisition and follow-up system for exterior cleaners across Australia. Our service typically includes Meta advertising strategy, exterior-cleaning ad creative, ad copy, audience targeting, conversion tracking, ongoing optimisation, LEO AI follow-up, automated SMS and email follow-up, and access to the 7FC Job Hub.
      </p>

      <h2>2. Fees and billing</h2>
      <p>
        Our management fee is <strong>AUD $250 per week + GST</strong>, billed weekly in advance. Ad spend is always paid by you directly to Meta on your own ad account — it is not paid through us. We recommend a minimum Meta ad budget from approximately $30 per day. No setup fee and no lock-in contract.
      </p>
      <p>
        There are no setup fees, no contracts and no lock-in periods. Either party may cancel the engagement at any time by written notice (email is fine), effective at the end of the then-current billing week. Fees already paid are non-refundable.
      </p>

      <h2>3. 30 exclusive local enquiries in 30 days guarantee</h2>
      <p>
        If your campaign does not generate at least 30 eligible local enquiries within its first 30 days live, we will continue managing your campaigns without charging our weekly management fee until it does. Ad spend remains your responsibility throughout. The guarantee applies only where you have followed our reasonable instructions (including ad budget, follow-up speed, and approval timeframes).
      </p>
      <p>An eligible enquiry is:</p>
      <ul>
        <li>A genuine person requesting an exterior-cleaning service;</li>
        <li>Located within the agreed service territory;</li>
        <li>Requesting a service included in the campaign;</li>
        <li>Providing usable contact information;</li>
        <li>Generated exclusively for you.</li>
      </ul>

      <h2>4. Your responsibilities</h2>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate information about your business, area and capacity;</li>
        <li>Maintain your own Meta Business Manager and ad account, and pay Meta directly for all ad spend;</li>
        <li>Respond to leads in a timely manner (we recommend within 5 minutes);</li>
        <li>Approve creative and campaigns within reasonable timeframes;</li>
        <li>Comply with all applicable laws, Meta&apos;s advertising policies and Australian consumer law.</li>
      </ul>

      <h2>5. Intellectual property &amp; creative</h2>
      <p>
        All creative work produced by us in the course of providing our services — including but not limited to ad creatives, copy, images, videos, scripts, landing pages, automations, funnel structures, prompts, frameworks, and any associated assets (collectively, the &quot;<strong>Creative</strong>&quot;) — remains <strong>the exclusive intellectual property of The 7 Figure Cleaner</strong> at all times.
      </p>
      <p>
        While our engagement is active and your fees are paid up to date, you are granted a <strong>limited, non-exclusive, non-transferable licence</strong> to use the Creative solely to promote your own business through our managed campaigns.
      </p>
      <p>
        Upon termination, cancellation, suspension or pause of the engagement for any reason, this licence <strong>immediately ends</strong>. You agree that you will <strong>not continue to run, reuse, copy, modify, repurpose, save, archive or redistribute</strong> any of the Creative in any form or on any platform after the engagement ends. Within 7 days of termination you will, on request, pause and remove any active campaigns containing our Creative and confirm you have done so. Any continued use after termination is a breach of these Terms and a breach of our copyright.
      </p>
      <p>
        Anything you supply to us (your logo, brand assets, photos, reviews, brand name) remains your property. You grant us a licence to use those materials only as necessary to deliver our services and, with your permission, in case studies or marketing.
      </p>

      <h2>6. Confidentiality</h2>
      <p>
        Each party agrees to keep the other party&apos;s confidential information confidential and to use it only for the purposes of this engagement. This obligation continues after the engagement ends.
      </p>

      <h2>7. Results &amp; warranties</h2>
      <p>
        We will perform our services with reasonable care and skill. We do not warrant any specific business outcome, revenue level or conversion rate beyond what is expressly stated in our 30-leads-in-30-days guarantee. To the maximum extent permitted by law, all other warranties (express or implied) are excluded.
      </p>

      <h2>8. Liability</h2>
      <p>
        To the maximum extent permitted by law, our total liability to you for any claim arising out of or in connection with these Terms or our services is limited to the management fees paid by you in the three (3) months immediately preceding the claim. We are not liable for any indirect, consequential, special or punitive losses, including loss of profits, loss of revenue or loss of opportunity.
      </p>
      <p>
        Nothing in these Terms limits any rights you may have under the Australian Consumer Law that cannot be excluded.
      </p>

      <h2>9. Suspension &amp; termination</h2>
      <p>
        We may suspend or terminate the engagement immediately if you breach these Terms, fail to pay fees when due, behave abusively toward our team, or use the Creative in a way not permitted by Section 5. Either party may otherwise terminate at any time as set out in Section 2.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These Terms are governed by the laws of Victoria, Australia. You and we submit to the exclusive jurisdiction of the courts of Victoria.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. The latest version will always be available on this page with the &quot;Last updated&quot; date. Continued use of the Site or our services after changes means you accept the updated Terms.
      </p>

      <h2>12. Contact</h2>
      <p>
        <strong>The 7 Figure Cleaner</strong> &middot; Chris Andrews<br />
        ABN 17 639 251 027<br />
        Carnegie VIC 3163, Melbourne, Australia<br />
        Email: <a href="mailto:hello@7figurecleaner.com.au">hello@7figurecleaner.com.au</a>
      </p>
    </LegalLayout>
  );
};

export default PrivacyPage;
